import { createContext, useContext, useEffect, useState } from 'react';
import * as React from 'react';
import { globalAppContext } from './global-app-context.tsx';
import { Logger } from '../../utils/logger/logger.ts';
import { safeWindow } from '../../utils/window.ts';
import { IGraphqlService } from '../graphql/graphql-service.ts';
import { WindowEventService } from '../../services/window-event-service.ts';
import { HistoryService } from '../../services/history-service.ts';
import { TimeOnSite } from '../tracking/time-on-site.ts';
import { Cookie } from '../../utils/cookie.ts';

export type SsrAppContext = {
  logger: Logger;
  graphqlService: IGraphqlService;
};

export type FrontendAppContext = SsrAppContext & {
  historyService: HistoryService;
  windowEventService: WindowEventService;
};

/**
 * The most universal type for the app context, that can be used anywhere without risks.
 *
 * It makes no assumptions about the environment, but still includes all possible dependencies as optionals for ease of use,
 * so that its possible to access all properties with optional chaining, regardless of the environment, like `appContext.userContext?.id`.
 */
export type AppContext = SsrAppContext & Partial<FrontendAppContext>;

/**
 * May be used in services that are part of an appContext themselves,
 * but still require dependencies to other parts of the appContext.
 */
export type PartialAppContext<T extends AppContext, P extends keyof T> = Pick<T, P>;

export const ReactAppContext = createContext<AppContext | undefined>(undefined);

export type AppContextProviderProps = {
  appContext: AppContext;
  /**
   * If given, the first render will use this context, which should be equivalent to the one
   * used for SSR, and then switch to the appContext prop. This ensures that hydration works correctly.
   */
  hydrationAppContext?: SsrAppContext;
};

export const AppContextProvider: React.FunctionComponent<
  React.PropsWithChildren<AppContextProviderProps>
> = props => {
  const [appContext, setAppContext] = useState<AppContext>(
    props.hydrationAppContext || props.appContext
  );

  useEffect(() => {
    if (props.hydrationAppContext) {
      const timeOnSite = new TimeOnSite(new WindowEventService());
      timeOnSite.start();
      setAppContext(props.appContext);
    }

    function activateHotjarRecording() {
      if (typeof window !== 'undefined' && window.hj) {
        let cohortName = 'sk-redesign';
        const abTestCohort = new Cookie('sk-AB-tabsetup').value;
        if (abTestCohort) {
          cohortName = 'sk-AB-tabsetup-' + abTestCohort;
        }
        const cohort = cohortName;

        if (cohort) {
          window.hj('event', cohort);
        }
      }
    }

    if (typeof window !== 'undefined' && window.__tcfapi && window.__cmp) {
      window.__tcfapi('getTCData', 2, (tcData: any, success: boolean) => {
        if (!success || !tcData) return;

        const cmpData = window.__cmp('getCMPData');
        let vendorConsent = false;
        if ('vendorConsents' in cmpData) {
          if ('s46' in cmpData.vendorConsents && cmpData.vendorConsents['s46']) {
            // s46 is Vendor-ID of Hotjar
            vendorConsent = true;
          }
        }

        const canTrack = tcData?.purpose?.consents?.[1] === true && vendorConsent;

        if (canTrack) {
          activateHotjarRecording();
        }
      });

      window.__cmp(
        'addEventListener',
        [
          'consentapproved',
          () => {
            activateHotjarRecording();
          },
          false
        ],
        null
      );
    }
  }, [props.appContext, props.hydrationAppContext]);

  return <ReactAppContext.Provider value={appContext}>{props.children}</ReactAppContext.Provider>;
};

export const useAppContext = <T extends AppContext>(): { appContext: T } => {
  const appContext = useContext(ReactAppContext) as T;

  if (appContext) {
    return { appContext };
  } else {
    if (safeWindow()) {
      return { appContext: globalAppContext<T>()! };
    } else {
      throw new Error(
        'useAppContext must be used within an AppContextProvider. No global context for fallback available.'
      );
    }
  }
};
